import React, { useState, useEffect } from "react";
import { Modal, Tooltip, message, Spin, Select } from "antd";
import { ChevronDown, ChevronUp, MessageCircleQuestion, X, Search, Edit3, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { GET_FAQS, Role_Account_Query } from "../../services/userAPI";
import { ADD_UPDATE_CLAN_FAQS } from "../../services/faqMutation";
import "./viewRole.css";
import PageBreadcrumb from '../../reusableField/PageBreadcrumb';
import DartsFaqsContent from "./Darts/masterConfiguration/userManagement/dartsfaqscontent";
import ClanLogo from "../../Images/clan-logo.png";
import DartsLogo from "../../Images/Darts-Logo.png";
import EditIcon from "../../Images/editIcon.png";
import DeleteIcon from "../../Images/deleteIcon.png";
import BackIcon from "../../Images/backIcon.png";
import HomeIcon from "../../Images/homeIcon.png";

const FAQPage = () => {
    const navigate = useNavigate();
    const location = useLocation();  // ✅ THIS LINE MUST BE HERE

    const { accountId, roleId, accountName, name } = location.state || {};
    const [inputType, setInputType] = useState(null);

    // ✅ STEP 1: useQuery FIRST
    const { data: roleData } = useQuery(Role_Account_Query, {
        variables: { accountId: accountId ? parseInt(accountId) : null },
        fetchPolicy: "network-only",
        skip: !accountId,
    });

    // ✅ STEP 2: then derive values FROM roleData
    const currentRole = roleData?.getAllJobrole?.find(
        (r) => String(r.id) === String(roleId)
    );
    const safeAccountName = accountName || roleData?.getAccountById?.name || '';
    const safeRoleName = name || currentRole?.name || '';
    const currentPath = location.pathname;

    const [faqActiveTab, setFaqActiveTab] = useState("DARTS");
    const [faqSearchTerm, setFaqSearchTerm] = useState("");
    const [showAddFaqModal, setShowAddFaqModal] = useState(false);
    const [newFaq, setNewFaq] = useState({ question: "", answer: "", product: "DARTS", jobRole: "all" });
    const [expandedFaqId, setExpandedFaqId] = useState(null);
    const [showEditFaqModal, setShowEditFaqModal] = useState(false);
    const [editingFaq, setEditingFaq] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deletingFaq, setDeletingFaq] = useState(null);
    const [activeTab, setActiveTab] = useState("faqs");
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 5;

    // Fetch FAQ data from API using lazy query for better control
    const [fetchFaqs, { data: apiData, loading: apiLoading, error: apiError }] = useLazyQuery(GET_FAQS, {
        fetchPolicy: "network-only"
    });

    // Separate query to fetch the other tab's count
    const otherTabRef = React.useRef(faqActiveTab === "DARTS" ? "CLAN" : "DARTS");
    const [fetchOtherTabCount] = useLazyQuery(GET_FAQS, {
        fetchPolicy: "network-only",
        onCompleted: (data) => {
            if (data?.getFaqs?.pageMeta?.totalCount !== undefined) {
                setFaqTotalCounts(prev => ({
                    ...prev,
                    [otherTabRef.current]: data.getFaqs.pageMeta.totalCount
                }));
            }
        }
    });

    // Helper to refetch current tab + other tab's count
    const refetchAllFaqs = () => {
        const otherTab = faqActiveTab === "DARTS" ? "CLAN" : "DARTS";
        otherTabRef.current = otherTab;
        fetchFaqs({
            variables: {
                accountId: accountId ? parseInt(accountId) : null,
                roleId: roleId ? parseInt(roleId) : null,
                searchTerm: faqSearchTerm || "",
                faqModule: faqActiveTab,
                pagingCriteria: {
                    limit: PAGE_SIZE,
                    page: currentPage,
                    sortOrder: "asc",
                    sortByField: "created_at"
                }
            }
        });
        fetchOtherTabCount({
            variables: {
                accountId: accountId ? parseInt(accountId) : null,
                roleId: roleId ? parseInt(roleId) : null,
                searchTerm: "",
                faqModule: otherTab,
                pagingCriteria: {
                    limit: 1,
                    page: 1,
                    sortOrder: "asc",
                    sortByField: "created_at"
                }
            }
        });
    };

    // Fetch data when page or search changes
    useEffect(() => {
        fetchFaqs({
            variables: {
                accountId: accountId ? parseInt(accountId) : null,
                roleId: roleId ? parseInt(roleId) : null,
                searchTerm: faqSearchTerm || "",
                faqModule: faqActiveTab,
                pagingCriteria: {
                    limit: PAGE_SIZE,
                    page: currentPage,
                    sortOrder: "asc",
                    sortByField: "created_at"
                }
            }
        });
    }, [currentPage, faqSearchTerm, fetchFaqs, accountId, roleId, faqActiveTab]);

    // Fetch the other tab's count on mount and when tab changes
    useEffect(() => {
        const otherTab = faqActiveTab === "DARTS" ? "CLAN" : "DARTS";
        otherTabRef.current = otherTab;
        fetchOtherTabCount({
            variables: {
                accountId: accountId ? parseInt(accountId) : null,
                roleId: roleId ? parseInt(roleId) : null,
                searchTerm: "",
                faqModule: otherTab,
                pagingCriteria: {
                    limit: 1,
                    page: 1,
                    sortOrder: "asc",
                    sortByField: "created_at"
                }
            }
        });
    }, [faqActiveTab, fetchOtherTabCount, accountId, roleId]);

    // FAQ data state
    const [faqData, setFaqData] = useState({
        DARTS: [],
        CLAN: [],
    });

    // Total counts for each tab (from API pageMeta)
    const [faqTotalCounts, setFaqTotalCounts] = useState({
        DARTS: 0,
        CLAN: 0,
    });

    // Map API data to FAQ structure
    useEffect(() => {
        if (apiData?.getFaqs?.data) {
            console.log("API Data received:", apiData.getFaqs.data);
            const faqs = [];

            apiData.getFaqs.data.forEach(faq => {
                // Check for active status (can be 1 or "1" or true)
                const isActive = faq.status === 1 || faq.status === "1" || faq.status === true;

                if (isActive) {
                    const mappedFaq = {
                        id: faq.id,
                        question: faq.faqParameter || "",
                        answer: faq.faqValue || "",
                        lang: faq.lang || "en",
                        faqtype: faq.faqtype,
                        accountId: faq.accountId
                    };
                    faqs.push(mappedFaq);
                }
            });

            console.log("Mapped FAQ data:", faqs);
            // Set data for the current active tab since API already filters by faqModule
            setFaqData(prev => ({
                ...prev,
                [faqActiveTab]: faqs
            }));
            // Update total count for the active tab from pageMeta
            if (apiData?.getFaqs?.pageMeta?.totalCount !== undefined) {
                setFaqTotalCounts(prev => ({
                    ...prev,
                    [faqActiveTab]: apiData.getFaqs.pageMeta.totalCount
                }));
            }
        }
    }, [apiData, faqActiveTab]);

    // Pagination info from API
    const pageMeta = apiData?.getFaqs?.pageMeta;
    const hasNextPage = pageMeta?.hasNextPage || false;
    const hasPreviousPage = pageMeta?.hasPreviousPage || false;
    const totalCount = pageMeta?.totalCount;
    const totalPages = pageMeta?.totalPages || 1;

    // Pagination handlers
    const handleNextPage = () => {
        if (hasNextPage) {
            setCurrentPage(prev => prev + 1);
            setExpandedFaqId(null);
        }
    };

    const handlePreviousPage = () => {
        if (hasPreviousPage) {
            setCurrentPage(prev => prev - 1);
            setExpandedFaqId(null);
        }
    };

    // Reset pagination when search or tab changes
    useEffect(() => {
        setCurrentPage(1);
    }, [faqSearchTerm, faqActiveTab]);

    const filteredFaqs = faqData[faqActiveTab] || [];

    // Add FAQ mutation
    const [addUpdateFaq, { loading: addFaqLoading }] = useMutation(ADD_UPDATE_CLAN_FAQS, {
        onCompleted: (data) => {
            if (data?.addUpdateClanFaqs?.success) {
                message.success(data.addUpdateClanFaqs.message || "FAQ added successfully!");
                setNewFaq({ question: "", answer: "", product: "DARTS", jobRole: "all" });
                setShowAddFaqModal(false);
                // Refetch FAQs and counts for both tabs
                refetchAllFaqs();
            } else {
                message.error(data?.addUpdateClanFaqs?.message || "Failed to add FAQ");
            }
        },
        onError: (error) => {
            message.error(error.message || "Failed to add FAQ");
        }
    });

    const handleAddFaq = () => {
        if (!newFaq.question.trim() || !newFaq.answer.trim() || !newFaq.jobRole) {
            message.error("Please fill in all required fields");
            return;
        }

        // Determine accountId and roleId based on jobRole selection
        const isAllJobRoles = newFaq.jobRole === "all";

        addUpdateFaq({
            variables: {
                accountId: isAllJobRoles ? null : (accountId ? parseInt(accountId) : null),
                roleId: isAllJobRoles ? null : (roleId ? parseInt(roleId) : null),
                faqValue: newFaq.answer.trim(),
                faqParameter: newFaq.question.trim(),
                faqId: null,
                faqLabel: newFaq.product
            }
        });
    };

    // Edit FAQ mutation
    const [editFaq, { loading: editFaqLoading }] = useMutation(ADD_UPDATE_CLAN_FAQS, {
        onCompleted: (data) => {
            if (data?.addUpdateClanFaqs?.success) {
                message.success(data.addUpdateClanFaqs.message || "FAQ updated successfully!");
                setEditingFaq(null);
                setShowEditFaqModal(false);
                // Refetch FAQs and counts for both tabs
                refetchAllFaqs();
            } else {
                message.error(data?.addUpdateClanFaqs?.message || "Failed to update FAQ");
            }
        },
        onError: (error) => {
            message.error(error.message || "Failed to update FAQ");
        }
    });

    const handleEditFaq = () => {
        if (!editingFaq || !editingFaq.question.trim() || !editingFaq.answer.trim()) {
            message.error("Please fill in both question and answer");
            return;
        }

        editFaq({
            variables: {
                accountId: editingFaq.accountId ? parseInt(editingFaq.accountId) : null,
                roleId: roleId ? parseInt(roleId) : null,
                faqValue: editingFaq.answer.trim(),
                faqParameter: editingFaq.question.trim(),
                faqId: parseInt(editingFaq.id),
                faqLabel: faqActiveTab
            }
        });
    };

    // Delete FAQ mutation
    const [deleteFaq, { loading: deleteFaqLoading }] = useMutation(ADD_UPDATE_CLAN_FAQS, {
        onCompleted: (data) => {
            if (data?.addUpdateClanFaqs?.success) {
                message.success("FAQ deleted successfully!");
                setDeletingFaq(null);
                setShowDeleteConfirm(false);
                setExpandedFaqId(null);
                // Refetch FAQs and counts for both tabs
                refetchAllFaqs();
            } else {
                message.error(data?.addUpdateClanFaqs?.message || "Failed to delete FAQ");
            }
        },
        onError: (error) => {
            message.error(error.message || "Failed to delete FAQ");
        }
    });

    const handleDeleteFaq = () => {
        if (!deletingFaq) return;

        deleteFaq({
            variables: {
                accountId: deletingFaq.accountId ? parseInt(deletingFaq.accountId) : null,
                roleId: roleId ? parseInt(roleId) : null,
                faqValue: deletingFaq.answer,
                faqParameter: deletingFaq.question,
                faqId: parseInt(deletingFaq.id),
                faqLabel: faqActiveTab,
                status: 0
            }
        });
    };

    const openEditModal = (faq, e) => {
        e.stopPropagation();
        setEditingFaq({ ...faq });
        setShowEditFaqModal(true);
    };

    const openDeleteConfirm = (faq, e) => {
        e.stopPropagation();
        setDeletingFaq(faq);
        setShowDeleteConfirm(true);
    };

    const productTabs = ["DARTS", "CLAN"];

    const handleBack = () => {
        navigate("/view-role", {
            state: { accountId, roleId, accountName, name }
        });
    };

    const handleBackToAdmin = () => {
        navigate("/admin"); // This will navigate back to the previous page
    }
    const dartsFormNeeded = location.state?.dartsFormNeeded;
    const pocName = location.state?.pocName;
    const pocEmail = location.state?.pocEmail;
    const pocNumber = location.state?.pocNumber;
    const numberLogin = location.state?.numberLogin;
    const emailLogin = location.state?.emailLogin;
    const mandatoryTimeDistribution = location.state?.mandatoryTimeDistribution;
    const targetEditDays = location.state?.targetEditDays;
    const inputEditDays = location.state?.inputEditDays;
    const dartsNeeded = location.state?.dartsNeeded;
    const clanNeeded = location.state?.clanNeeded;
    const syncNeeded = location.state?.syncNeeded;
    const geoTracking = location.state?.geoTracking;
    const signInSignOutRequired = location.state?.signInSignOutRequired;
    const locationRequired = location.state?.locationRequired;
    const jobroleAccessUserIds = location.state?.jobroleAccessUserIds;
    const ldmsNeeded = location.state?.ldmsNeeded;
    const mirrorNeeded = location.state?.mirrorNeeded;


    const handleBackToViewRole = () => {
        navigate("/productsConfiguration", {
            state: {
                roleId: roleId ?? location.state?.roleId,
                accountId: accountId ?? location.state?.accountId,
                accountName: accountName ?? location.state?.accountName,
                name: name ?? location.state?.name,
                dartsNeeded: inputType ? true : (dartsNeeded ?? location.state?.dartsNeeded ?? false),
                clanNeeded: clanNeeded ?? location.state?.clanNeeded ?? false,
                syncNeeded: syncNeeded ?? location.state?.syncNeeded ?? false,
                dartsFormNeeded: inputType ? inputType === "Forms" : (dartsFormNeeded ?? location.state?.dartsFormNeeded ?? false),
                pocName: pocName ?? location.state?.pocName,
                pocEmail: pocEmail ?? location.state?.pocEmail,
                pocNumber: pocNumber ?? location.state?.pocNumber,
                numberLogin: numberLogin ?? location.state?.numberLogin ?? false,
                emailLogin: emailLogin ?? location.state?.emailLogin ?? false,
                mandatoryTimeDistribution: mandatoryTimeDistribution ?? location.state?.mandatoryTimeDistribution ?? false,
                targetEditDays: targetEditDays ?? location.state?.targetEditDays ?? 0,
                inputEditDays: inputEditDays ?? location.state?.inputEditDays ?? 0,
                geoTracking: geoTracking ?? location.state?.geoTracking ?? false,
                signInSignOutRequired: signInSignOutRequired ?? location.state?.signInSignOutRequired ?? false,
                locationRequired: locationRequired ?? location.state?.locationRequired ?? false,
                jobroleAccessUserIds: jobroleAccessUserIds ?? location.state?.jobroleAccessUserIds ?? [],
                ldmsNeeded: ldmsNeeded ?? location.state?.ldmsNeeded ?? false,
                mirrorNeeded: mirrorNeeded ?? location.state?.mirrorNeeded ?? false,
            },
        });
    };

    const searchedFaqs = faqSearchTerm?.trim()
        ? filteredFaqs.filter((faq) =>
            faq.question.toLowerCase().includes(faqSearchTerm.toLowerCase())
        )
        : filteredFaqs;

    return (
        <div className="min-h-screen" >
            <div className="user-profile-container faq-page" style={{ paddingTop: 105, marginTop: 0 }}>
                {/* Breadcrumb and Back Button */}
              <div className="mx-auto px-3">

                    <div className="flex justify-between items-center w-full  mx-2">

                        {/* Back Button */}
                        <button
                            onClick={handleBackToViewRole}
                            className="flex items-center gap-2 bg-[#C21A17] text-white px-5 py-2 rounded-lg text-sm font-semibold shadow-md hover:bg-[#A91513] transition-all duration-200"
                        >
                            <img
                                src={BackIcon}
                                alt="Back"
                                className="w-[15px] h-[15px] object-contain"
                            />
                            Back
                        </button>

                        {/* Home Button */}
                        <button
                            onClick={() => navigate("/admin")}
                            className="flex items-center gap-2 bg-[#C21A17] text-white px-5 py-2 mr-4 rounded-lg text-sm font-semibold shadow-md hover:bg-[#A91513] transition-all duration-200"
                        >
                            <img
                                src={HomeIcon}
                                alt="Home"
                                className="w-[15px] h-[15px] object-contain"
                            />
                            Home
                        </button>

                    </div>
                    <div
                        className="flex items-center text-sm text-[#939393]"
                        style={{ marginTop: 12, marginLeft: 11 }}
                    >
                        <span
                            onClick={() => navigate("/admin")}
                            className="cursor-pointer"
                        >
                            {safeAccountName}
                        </span>

                        <span className="mx-2 text-[14px]">
                            ›
                        </span>

                        <span
                            onClick={() =>
                                navigate("/dashboard", {
                                    state: {
                                        roleId,
                                        accountId,
                                        name,
                                        accountName,
                                    },
                                })
                            }
                            className="cursor-pointer"
                        >
                            {safeRoleName}
                        </span>

                        <span className="mx-2 text-[14px]">
                            ›
                        </span>

                        {/* Experience Configuration */}
                        <span
                            onClick={() =>
                                navigate("/experience-configuration", {
                                    state: {
                                        roleId,
                                        accountId,
                                        name,
                                        accountName,
                                    },
                                })
                            }
                            className="cursor-pointer"
                        >
                            Experience Configuration
                        </span>

                        <span className="mx-2 text-[14px]">
                            ›
                        </span>

                        {/* Product Configuration */}
                        <span
                            onClick={() =>
                                navigate("/productsConfiguration", {
                                    state: {
                                        roleId,
                                        accountId,
                                        name,
                                        accountName,
                                    },
                                })
                            }
                            className="cursor-pointer"
                        >
                            Product Configuration
                        </span>

                        <span className="mx-2 text-[14px]">
                            ›
                        </span>

                        <span className="font-semibold text-gray-800">
                            Clan
                        </span>
                    </div>
                </div>


                <div className="w-full flex justify-center my-6">
                    <div className="flex items-center gap-3 p-1.5 bg-white border border-gray-300 rounded-2xl shadow-sm">

                        <button
                            onClick={() =>
                                navigate('/products-Configuration', {
                                    state: {
                                        ...location.state,
                                    },
                                })
                            }
                            className="px-5 py-1 rounded-lg text-base font-semibold bg-red-50 text-red-600"
                            style={{
                                background: currentPath === '/products-Configuration' ? '#fee2e2' : 'transparent',
                                color: currentPath === '/products-Configuration' ? '#b91c1c' : '#535659',
                            }}
                        >
                            Darts
                        </button>

                        <button
                            className="px-5 py-1 rounded-lg text-base font-semibold text-gray-500"
                            style={{
                                background: currentPath === '/faqs' ? '#fee2e2' : 'transparent',
                                color: currentPath === '/faqs' ? '#b91c1c' : '#535659',
                            }}
                        >
                            FAQ's
                        </button>

                        <button
                            onClick={() =>
                                navigate('/clan-management', {
                                    state: {
                                        safeAccountName,
                                        safeRoleName,
                                        accountName,
                                        name,
                                        accountId,
                                        roleId,
                                        activeTab: '1',
                                    },
                                })
                            }
                            className="px-5 py-1 rounded-lg text-base font-semibold"
                            style={{
                                background: currentPath === '/clan-management' ? '#fee2e2' : 'transparent',
                                color: currentPath === '/clan-management' ? '#b91c1c' : '#6e7173',
                            }}
                        >
                            Content
                        </button>

                    </div>
                </div>

                {/* Two Column Layout */}
                <div className="mx-auto px-3 pb-6 mt-1">
                    <div className="grid grid-cols-12 gap-6">
                        {/* Left Sidebar - Product Selector & Stats */}
                        <div className="col-span-3">
                            <div className="bg-white rounded-xl border border-[#E5E7EB] p-4 mb-2 shadow-[0px_4px_12px_rgba(0,0,0,0.08)]">                                {/* Product Tabs - Vertical */}

                                {/* Stats Card */}
                                <div
                                    onClick={() => setFaqActiveTab("CLAN")}
                                    className={`bg-white rounded-sm border p-7 pr-7 pl-10 mb-4 cursor-pointer shadow-[0px_3px_8px_rgba(0,0,0,0.06)]
                                        ${faqActiveTab === "CLAN"
                                            ? "border-[#AD0101] bg-red-50"
                                            : "border-[#E5E7EB]"
                                        }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <img
                                            src={ClanLogo}
                                            alt="Clan"
                                            className="h-9 w-auto object-contain ml-[-30px]"
                                        />
                                        <span className="text-[28px] font-bold text-[#C00000] leading-none mt-[-5px]">
                                            {faqTotalCounts.CLAN || 0}
                                        </span>
                                    </div>
                                </div>

                                <div
                                    onClick={() => setFaqActiveTab("DARTS")}
                                    className={`bg-white rounded-sm border p-8 mb-4 cursor-pointer shadow-[0px_3px_8px_rgba(0,0,0,0.06)]
                                        ${faqActiveTab === "DARTS"
                                            ? "border-[#AD0101] bg-red-50"
                                            : "border-[#E5E7EB]"
                                        }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <img
                                            src={DartsLogo}
                                            alt="Darts"
                                            className="h-6 w-auto object-contain ml-[-21px]"
                                        />

                                        <span className="text-[28px] font-bold text-[#C00000] leading-none">
                                            {faqTotalCounts.DARTS || 0}
                                        </span>
                                    </div>
                                </div>

                                <div className="bg-[#FAF5F5] rounded-sm border border-[#EEDDDD] p-8 shadow-shadow-[0_3px_8px_rgba(0,0,0,0.06)]">
                                    <div className="flex items-center justify-between">
                                        <span className="text-[15px] font-semibold text-[#374151]">
                                            Total FAQ's
                                        </span>

                                        <span className="text-[28px] font-bold text-[#C00000] leading-none">
                                            {(faqTotalCounts.CLAN || 0) +
                                                (faqTotalCounts.DARTS || 0)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Content - Search & FAQs */}
                        <div className="col-span-9">
                            <div className="bg-white rounded-xl p-5 shadow-sm space-y-4">
                                {/* Search Bar with Add FAQ Button */}
                                <div className="flex items-center gap-3">
                                    <div className="relative flex-1">
                                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text"
                                            placeholder="Search questions..."
                                            value={faqSearchTerm}
                                            onChange={(e) => setFaqSearchTerm(e.target.value)}
                                            className="w-full py-2 pl-10 pr-10 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none focus:bg-white focus:ring-2 focus:ring-[#F9FAFB]/20 transition-all"
                                        />
                                        {faqSearchTerm && (
                                            <button
                                                onClick={() => setFaqSearchTerm("")}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                            >
                                                <X size={14} />
                                            </button>
                                        )}
                                    </div>
                                    <button
                                        className="flex items-center gap-2 px-4 py-2 bg-[#D00000] text-white rounded-sm text-sm font-semibold transition-all whitespace-nowrap"
                                        onClick={() => setShowAddFaqModal(true)}
                                    >
                                        Add FAQ
                                    </button>
                                </div>

                                {/* FAQ List */}
                                <div className="space-y-3">
                                    {apiLoading ? (
                                        <div className="flex flex-col items-center justify-center py-20 text-center bg-white rounded-2xl border border-gray-200">
                                            <div className="loading-container">
                                                <div className="spinner"></div>
                                            </div>
                                            <p className="text-lg font-semibold text-gray-500 mt-4">Loading FAQs...</p>
                                        </div>
                                    ) : apiError ? (
                                        <div className="flex flex-col items-center justify-center py-20 text-center bg-white rounded-2xl border border-red-200 bg-red-50">
                                            <MessageCircleQuestion size={64} className="text-red-300" />
                                            <p className="text-lg font-semibold text-red-600 mt-4 mb-2">Error loading FAQs</p>
                                            <span className="text-sm text-red-500">{apiError.message}</span>
                                            <button
                                                onClick={() => fetchFaqs({
                                                    variables: {
                                                        accountId: accountId ? parseInt(accountId) : null,
                                                        roleId: roleId ? parseInt(roleId) : null,
                                                        searchTerm: faqSearchTerm || null,
                                                        faqModule: faqActiveTab,
                                                        pagingCriteria: {
                                                            limit: PAGE_SIZE,
                                                            page: currentPage,
                                                            sortOrder: "asc",
                                                            sortByField: "created_at"
                                                        }
                                                    }
                                                })}
                                                className="mt-4 px-4 py-2 bg-[#AD0101] text-white rounded-lg text-sm font-semibold hover:bg-[#b00000] transition-all"
                                            >
                                                Retry
                                            </button>
                                        </div>
                                    ) : searchedFaqs.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-20 text-center bg-white rounded-2xl border border-gray-200">
                                            <MessageCircleQuestion size={64} className="text-gray-300" />
                                            <p className="text-lg font-semibold text-gray-500 mt-4 mb-2">No FAQs found</p>
                                            <span className="text-base text-gray-400">Try a different search term or add a new FAQ</span>
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            {searchedFaqs.map((faq, index) => (
                                                <div
                                                    key={faq.id}
                                                    className={`bg-white border-2 rounded-2xl overflow-hidden transition-all duration-300 ${expandedFaqId === faq.id
                                                        ? 'border-[#AD0101] shadow-lg shadow-red-100'
                                                        : 'border-gray-200 hover:border-[#AD0101] hover:shadow-md'
                                                        }`}
                                                >
                                                    <div
                                                        className="flex justify-between items-start p-5 cursor-pointer hover:bg-gray-50 transition-colors"
                                                        onClick={() => setExpandedFaqId(expandedFaqId === faq.id ? null : faq.id)}
                                                    >
                                                        <div className="flex-1 pr-4">
                                                            <span className="text-[#AD0101] font-bold mr-2 text-lg">Q{((currentPage - 1) * PAGE_SIZE) + index + 1}.</span>
                                                            <span className="text-base font-medium text-gray-700 leading-relaxed">{faq.question}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2 flex-shrink-0">
                                                            <Tooltip title="Edit">
                                                                <button
                                                                    onClick={(e) => openEditModal(faq, e)}
                                                                    className="p-2 rounded-lg text-gray-400 hover:text-[#AD0101] hover:bg-red-50 transition-all duration-200"
                                                                >
                                                                    <img
                                                                        src={EditIcon}
                                                                        alt="Edit"
                                                                        className="w-[18px] h-[18px] object-contain"
                                                                    />
                                                                </button>
                                                            </Tooltip>
                                                            <Tooltip title="Delete">
                                                                <button
                                                                    onClick={(e) => openDeleteConfirm(faq, e)}
                                                                    className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-all duration-200"
                                                                >
                                                                    <img
                                                                        src={DeleteIcon}
                                                                        alt="Delete"
                                                                        className="w-[18px] h-[18px] object-contain"
                                                                    />
                                                                </button>
                                                            </Tooltip>
                                                            <div className="text-[#B7B6B6] ml-2">
                                                                {expandedFaqId === faq.id ? (
                                                                    <ChevronUp size={24} />
                                                                ) : (
                                                                    <ChevronDown size={24} />
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {expandedFaqId === faq.id && (
                                                        <div className="px-5 pb-5 bg-gradient-to-b from-gray-50 to-white border-t border-dashed border-gray-200">
                                                            <div className="text-sm font-semibold text-[#AD0101] uppercase tracking-wide mt-4 mb-3">
                                                                Answer:
                                                            </div>
                                                            <p className="text-base text-gray-600 leading-relaxed">{faq.answer}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                </div>

                            </div>

                                {/* Pagination Controls */}
                                {filteredFaqs.length > 0 && totalPages > 0 && (
                                    <div className="mt-3 flex justify-end pr-2">
                                        <div className="flex items-center gap-1">
                                                <button
                                                    onClick={handlePreviousPage}
                                                    disabled={!hasPreviousPage}
                                                    className={`flex items-center justify-center w-8 h-8 text-gray-500 transition-colors ${!hasPreviousPage
                                                        ? 'cursor-not-allowed text-gray-400'
                                                        : 'hover:text-[#AD0101]'
                                                        }`}
                                                >
                                                    <ChevronLeft size={16} />
                                                </button>
                                                {(() => {
                                                    const pages = [];
                                                    const maxVisiblePages = 5;
                                                    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                                                    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

                                                    if (endPage - startPage + 1 < maxVisiblePages) {
                                                        startPage = Math.max(1, endPage - maxVisiblePages + 1);
                                                    }

                                                    if (startPage > 1) {
                                                        pages.push(
                                                            <button
                                                                key={1}
                                                                onClick={() => setCurrentPage(1)}
                                                                className="w-8 h-8 rounded-md text-sm font-medium bg-transparent text-gray-700 hover:text-[#AD0101] transition-colors"
                                                            >
                                                                1
                                                            </button>
                                                        );
                                                        if (startPage > 2) {
                                                            pages.push(<span key="start-ellipsis" className="px-2 text-gray-400">...</span>);
                                                        }
                                                    }

                                                    for (let i = startPage; i <= endPage; i++) {
                                                        pages.push(
                                                            <button
                                                                key={i}
                                                                onClick={() => setCurrentPage(i)}
                                                                className={`w-8 h-8 rounded-md border text-sm font-medium transition-colors ${currentPage === i
                                                                    ? 'border-gray-300 bg-white text-[#AD0101]'
                                                                    : 'border-transparent bg-transparent text-gray-700 hover:text-[#AD0101]'
                                                                    }`}
                                                            >
                                                                {i}
                                                            </button>
                                                        );
                                                    }

                                                    if (endPage < totalPages) {
                                                        if (endPage < totalPages - 1) {
                                                            pages.push(<span key="end-ellipsis" className="px-2 text-gray-400">...</span>);
                                                        }
                                                        pages.push(
                                                            <button
                                                                key={totalPages}
                                                                onClick={() => setCurrentPage(totalPages)}
                                                                className="w-8 h-8 rounded-md text-sm font-medium bg-transparent text-gray-700 hover:text-[#AD0101] transition-colors"
                                                            >
                                                                {totalPages}
                                                            </button>
                                                        );
                                                    }

                                                    return pages;
                                                })()}
                                                <button
                                                    onClick={handleNextPage}
                                                    disabled={!hasNextPage}
                                                    className={`flex items-center justify-center w-8 h-8 text-gray-500 transition-colors ${!hasNextPage
                                                        ? 'cursor-not-allowed text-gray-400'
                                                        : 'hover:text-[#AD0101]'
                                                        }`}
                                                >
                                                    <ChevronRight size={16} />
                                                </button>
                                        </div>
                                    </div>
                                )}
                        </div>
                    </div>
                </div>

                {/* Add FAQ Modal */}
                <Modal
                    title={
                        <div className="flex items-center gap-3">
                            <span className="text-lg font-semibold">Add New FAQ</span>
                        </div>
                    }
                    open={showAddFaqModal}
                    onCancel={() => {
                        setShowAddFaqModal(false);
                        setNewFaq({ question: "", answer: "", product: "DARTS", jobRole: "all" });
                    }}
                    footer={null}
                    width={400}
                    centered
                    styles={{ body: { padding: 16 } }}
                >
                    <div className="py-2">
                        {/* Product Selector */}
                        <div className="mb-2">
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Product</label>
                            <div className="flex gap-3">
                                {productTabs.map((product) => (
                                    <div
                                        key={product}
                                        className={`flex flex-col items-center gap-1 px-5 py-2 border-2 rounded-xl cursor-pointer transition-all duration-300 flex-1 ${newFaq.product === product
                                            ? 'border-[#AD0101] bg-gradient-to-b from-red-50 to-red-100'
                                            : 'border-gray-200 hover:border-red-200 hover:bg-red-50'
                                            }`}
                                        onClick={() => setNewFaq(prev => ({ ...prev, product }))}
                                    >
                                        {/* <img src={getProductIcon(product)} alt={product} className="w-5 h-5 object-contain" /> */}
                                        <span className={`text-xs font-semibold ${newFaq.product === product ? 'text-[#AD0101]' : 'text-gray-600'}`}>
                                            {product}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Job Role Dropdown */}
                        <div className="mb-2">
                            <label className="block text-sm font-semibold text-gray-700 mb-1">
                                Job Role <span className="text-[#AD0101]">*</span>
                            </label>
                            <Select
                                value={newFaq.jobRole}
                                onChange={(value) => setNewFaq(prev => ({ ...prev, jobRole: value }))}
                                className="w-full"
                                placeholder="Select Job Role"
                                showSearch
                                allowClear
                                optionFilterProp="label"
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={[
                                    ...(name ? [{ value: name, label: `Specific to ${name}` }] : []),
                                    { value: 'all', label: 'All Job Roles' }
                                ]}
                            />
                        </div>

                        {/* Question Field */}
                        <div className="mb-2">
                            <label className="block text-sm font-semibold text-gray-700 mb-1">
                                Question <span className="text-[#AD0101]">*</span>
                            </label>
                            <textarea
                                placeholder="Enter your question..."
                                value={newFaq.question}
                                onChange={(e) => setNewFaq(prev => ({ ...prev, question: e.target.value }))}
                                rows={2}
                                className="w-full h-20 p-3 border-2 border-gray-200 rounded-xl text-sm resize-none"
                            />
                        </div>

                        {/* Answer Field */}
                        <div className="mb-2">
                            <label className="block text-sm font-semibold text-gray-700 mb-1">
                                Answer <span className="text-[#AD0101]">*</span>
                            </label>
                            <textarea
                                placeholder="Enter the answer..."
                                value={newFaq.answer}
                                onChange={(e) => setNewFaq(prev => ({ ...prev, answer: e.target.value }))}
                                rows={2}
                                className="w-full h-24 p-3 border-2 border-gray-200 rounded-xl text-sm resize-none"
                            />
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3 justify-end">
                            <button
                                className="px-6 py-2 border-2 border-gray-200 bg-white rounded-lg text-sm font-medium text-gray-600 hover:border-[#AD0101] hover:text-[#AD0101] transition-all duration-300"
                                onClick={() => {
                                    setShowAddFaqModal(false);
                                    setNewFaq({ question: "", answer: "", product: "DARTS", jobRole: "all" });
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className={`px-6 py-2 rounded-lg text-sm font-semibold text-white transition-all duration-300 ${!newFaq.question.trim() || !newFaq.answer.trim() || !newFaq.jobRole || addFaqLoading
                                    ? 'bg-gray-300 cursor-not-allowed'
                                    : 'bg-[#AD0101] hover:-translate-y-0.5 hover:shadow-lg hover:shadow-red-200'
                                    }`}
                                onClick={handleAddFaq}
                                disabled={!newFaq.question.trim() || !newFaq.answer.trim() || !newFaq.jobRole || addFaqLoading}
                            >
                                {addFaqLoading ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : 'Add FAQ'}
                            </button>
                        </div>
                    </div>
                </Modal>

                {/* Edit FAQ Modal */}
                <Modal
                    title={
                        <div className="flex items-center gap-3">
                            <span className="text-lg font-semibold">Edit FAQ</span>
                        </div>
                    }
                    open={showEditFaqModal}
                    onCancel={() => {
                        setShowEditFaqModal(false);
                        setEditingFaq(null);
                    }}
                    footer={null}
                    width={400}
                    centered
                    styles={{ body: { padding: 16 } }}
                >
                    {editingFaq && (
                        <div className="py-2">
                            {/* Current Product Badge */}
                            <div className="mb-3">
                                <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-50 to-red-100 border-2 border-[#AD0101] rounded-xl w-fit">
                                    {/* <img src={getProductIcon(faqActiveTab)} alt={faqActiveTab} className="w-6 h-6 object-contain" /> */}
                                    <span className="text-sm font-semibold text-[#AD0101]">{faqActiveTab}</span>
                                </div>
                            </div>

                            {/* Question Field */}
                            <div className="mb-2">
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Question <span className="text-[#AD0101]">*</span>
                                </label>
                                <textarea
                                    placeholder="Enter your question..."
                                    value={editingFaq.question}
                                    onChange={(e) => setEditingFaq(prev => ({ ...prev, question: e.target.value }))}
                                    rows={3}
                                    className="w-full h-20 p-3 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-[#AD0101] focus:ring-2 focus:ring-[#AD0101]/10 transition-all duration-300 resize-none"
                                />
                            </div>

                            {/* Answer Field */}
                            <div className="mb-2">
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Job Role<span className="text-[#AD0101]">*</span>
                                </label>
                                <textarea
                                    placeholder="Enter the answer..."
                                    value={editingFaq.answer}
                                    onChange={(e) => setEditingFaq(prev => ({ ...prev, answer: e.target.value }))}
                                    rows={3}
                                    className="w-full h-24 p-3 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-[#AD0101] focus:ring-2 focus:ring-[#AD0101]/10 transition-all duration-300 resize-none"
                                />
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-3 justify-end pt-3 border-t border-gray-100">
                                <button
                                    className="px-6 py-2 border-2 border-gray-200 bg-white rounded-lg text-sm font-medium text-gray-600 hover:border-gray-400 hover:text-gray-700 transition-all duration-300"
                                    onClick={() => {
                                        setShowEditFaqModal(false);
                                        setEditingFaq(null);
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    className={`px-6 py-2 rounded-lg text-sm font-semibold text-white transition-all duration-300 ${!editingFaq.question.trim() || !editingFaq.answer.trim() || editFaqLoading
                                        ? 'bg-gray-300 cursor-not-allowed'
                                        : 'bg-[#AD0101] hover:-translate-y-0.5 hover:shadow-lg hover:shadow-red-200'
                                        }`}
                                    onClick={handleEditFaq}
                                    disabled={!editingFaq.question.trim() || !editingFaq.answer.trim() || editFaqLoading}
                                >
                                    {editFaqLoading ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : 'Save Changes'}
                                </button>
                            </div>
                        </div>
                    )}
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal
                    title={
                        <div className="flex items-center gap-3">
                            {/* <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                                <Trash2 size={20} className="text-red-600" />
                            </div> */}
                            <span className="text-lg font-bold text-gray-800">Delete FAQ</span>
                        </div>
                    }
                    open={showDeleteConfirm}
                    onCancel={() => {
                        setShowDeleteConfirm(false);
                        setDeletingFaq(null);
                    }}
                    footer={null}
                    width={450}
                    style={{ top: 40 }}
                >
                    <div className="py-4">
                        <p className="text-lightblack-800 mb-4">
                            Are you sure you want to delete this FAQ?
                        </p>
                        {deletingFaq && (
                            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-5">
                                <div className="text-sm font-semibold text-[#AD0101] mb-1">Question:</div>
                                <p className="text-sm text-[#AD0101]">{deletingFaq.question}</p>
                            </div>
                        )}
                        <div className="flex gap-3 justify-end pt-4 border-t border-gray-100">
                            <button
                                className="px-6 py-2.5 border-2 border-gray-200 bg-white rounded-lg text-sm font-medium text-gray-600 hover:border-gray-400 hover:text-gray-700 transition-all duration-300"
                                onClick={() => {
                                    setShowDeleteConfirm(false);
                                    setDeletingFaq(null);
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className={`px-6 py-2.5 rounded-lg text-sm font-semibold text-white transition-all duration-300 ${deleteFaqLoading ? 'bg-gray-300 cursor-not-allowed' : 'bg-[#AD0101] hover:-translate-y-0.5 hover:shadow-lg hover:shadow-red-200'}`}
                                onClick={handleDeleteFaq}
                                disabled={deleteFaqLoading}
                            >
                                {deleteFaqLoading ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : 'Delete FAQ'}
                            </button>
                        </div>
                    </div>
                </Modal>
            </div>
        </div>

    );
};

export default FAQPage;
